import { useEffect, useState } from 'react';

/**
 * Whether the dark theme is currently applied.
 *
 * Reads the `.dark` class the ThemeProvider sets on <html> - the resolved
 * source of truth, so it also covers `mode: 'system'`. Reactive: a
 * MutationObserver repaints consumers when the theme flips at runtime.
 *
 * Use this for anything that can't consume Tailwind `dark:` variants - e.g.
 * a literal color handed to a canvas/SVG/3rd-party widget (Recharts,
 * react-spinners, Stripe Elements' `appearance`).
 */
export function useIsDark(): boolean {
  const [isDark, setIsDark] = useState(
    () =>
      typeof document !== 'undefined' &&
      document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setIsDark(el.classList.contains('dark'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}
